
import { useState, useEffect, useCallback, memo } from "react";
import { motion } from "framer-motion";
import { MainChartContent } from "./components/MainChartContent";

interface GrowthLineChartProps {
  data: any[];
  showAverageLine: boolean;
  averageData: any[];
  displayOriginalValue: number | null;
  valueKey: string;
  unitLabel: string;
  yAxisDomain: [number, number] | ['auto', 'auto'];
  chartConfig: any;
}

// Use memo to prevent unnecessary re-renders
export const GrowthLineChart = memo(({
  data,
  showAverageLine,
  averageData,
  displayOriginalValue,
  valueKey,
  unitLabel,
  yAxisDomain,
  chartConfig
}: GrowthLineChartProps) => {
  const isHeight = valueKey === "displayHeight";
  const [animationActive, setAnimationActive] = useState(true);
  
  // Reset animation when data changes, with debounce - optimize to use less resources
  useEffect(() => {
    if (!data || data.length === 0) return;
    
    setAnimationActive(false);
    
    const timer = window.setTimeout(() => {
      setAnimationActive(true);
    }, 100); // Reduced from 30ms and using setTimeout directly
    
    return () => {
      clearTimeout(timer);
    };
  }, [data, showAverageLine, unitLabel]);
  
  return (
    <motion.div 
      className="w-full h-full"
      initial={{ opacity: 0.6 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <MainChartContent
        data={data}
        showAverageLine={showAverageLine}
        averageData={averageData}
        displayOriginalValue={displayOriginalValue}
        valueKey={valueKey}
        unitLabel={unitLabel}
        yAxisDomain={yAxisDomain}
        chartConfig={chartConfig}
        animationActive={animationActive}
        isHeight={isHeight}
      />
    </motion.div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function to prevent unnecessary re-renders
  // Only re-render if these specific props changed
  return (
    prevProps.data.length === nextProps.data.length &&
    prevProps.showAverageLine === nextProps.showAverageLine &&
    prevProps.displayOriginalValue === nextProps.displayOriginalValue &&
    prevProps.unitLabel === nextProps.unitLabel &&
    prevProps.valueKey === nextProps.valueKey
  );
});

GrowthLineChart.displayName = "GrowthLineChart";
